import { type ReactNode } from "react";

import { MAX_QUANTITY_LENGTH } from "../../../utils/constants";
import { useTranslation } from "react-i18next";
import { Box, styled } from "~/common/mui";
import NumberField from "../NumberField/NumberField";
import NumberFieldCompact from "../NumberField/NumberFieldCompact";

interface UseQuantityInputProps {
  value?: number | null;
  step: number;
  onValueChange?: (newValue: number) => void;
  hideButtons?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  persistentMinValue?: boolean;
  roundToStep?: boolean;
}

function useQuantityInput({ disabled, min, onValueChange, persistentMinValue }: UseQuantityInputProps) {
  const onInputChange = (newVal: number | undefined) => {
    if (newVal === undefined || isNaN(newVal)) {
      if (persistentMinValue && min !== undefined) {
        onValueChange?.(min);
      }
      return;
    }

    const digits = Math.abs(newVal).toString().replace(".", "").length;
    if (digits > MAX_QUANTITY_LENGTH) {
      return;
    }

    onValueChange?.(newVal);
  };

  return {
    onInputChange,
    inputDisabled: disabled,
  };
}

const clamp = (
  val: number,
  { min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY }: { min?: number; max?: number },
) => Math.max(min, Math.min(max, val));

export interface QuantityInputProps extends UseQuantityInputProps {
  id?: string;
  onBlur?: () => void;
  className?: string;
  error?: boolean;
  label?: ReactNode;
  helperLabel?: ReactNode;
  "data-test-id"?: string;
  startAdornment?: ReactNode;
  variant?: "primary" | "compact";
  persistentMinValue?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  showErrorIcon?: boolean;
}

const Root = styled("div")({
  display: "flex",
  position: "relative",
  border: "1px solid transparent",
});

export const QuantityInput = ({
  className,
  "data-test-id": testId,
  id,
  error,
  label,
  helperLabel,
  hideButtons,
  variant,
  size = "small",
  roundToStep = true,
  showErrorIcon = false,
  ...rest
}: QuantityInputProps) => {
  const { t } = useTranslation();

  const { onInputChange, inputDisabled } = useQuantityInput(rest);

  const onBlur = () => {
    if (rest.value !== undefined && rest.value !== null) {
      const clampedVal = clamp(rest.value, rest);
      if (clampedVal !== rest.value) {
        rest.onValueChange?.(clampedVal);
      }
    }

    rest.onBlur?.();
  };

  const { step, min, max } = rest;

  if (variant === "compact") {
    return (
      <Box className={className}>
        <NumberFieldCompact
          label={label ?? t("input.quantityPieces")}
          value={rest.value}
          id={id}
          onValueChange={(value: number | null) => {
            const newValue = value === null ? undefined : value;
            onInputChange(newValue);
          }}
          disabled={inputDisabled}
          error={error}
          helperText={helperLabel}
          data-test-id={testId}
          roundToStep={roundToStep}
          onBlur={onBlur}
          size={size}
          step={step}
          min={min}
          max={max}
          showErrorIcon={showErrorIcon}
        />
      </Box>
    );
  }

  return (
    <Root className={className}>
      <NumberField
        label={label ?? t("input.quantity")}
        value={rest.value}
        id={id}
        onValueChange={(value: number | null) => {
          const newValue = value === null ? undefined : value;
          onInputChange(newValue);
        }}
        disabled={inputDisabled}
        error={error}
        helperText={helperLabel}
        data-test-id={testId}
        onBlur={onBlur}
        roundToStep={roundToStep}
        size={size}
        step={step}
        min={min}
        max={max}
        hideButtons={hideButtons}
        showErrorIcon={showErrorIcon}
      />
    </Root>
  );
};
